// ═══════════════════════════════════════════════
// Odoo API — Appels sécurisés via /odoo-api
// Plus de proxy générique, plus d'URL client
// Les fonctions gardent les mêmes signatures pour compat
// ═══════════════════════════════════════════════

const API_URL = '/odoo-api';
const ELIXIR_COMPANY_ID = 2;

export async function callAction(action, params) {
  const res = await fetch(API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include', // envoyer le cookie HttpOnly
    body: JSON.stringify({ action, params }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'HTTP ' + res.status);
  }
  const json = await res.json();
  if (json.error) throw new Error(json.error);
  return json.result;
}

// ── Compat: odooRpc pour les modules pas encore migrés ──
// Route tout via callAction avec des actions métier spécifiques
export async function odooRpc(url, endpoint, params) {
  if (endpoint === '/web/session/authenticate') {
    return { uid: 1 };
  }
  if (endpoint !== '/web/dataset/call_kw') {
    throw new Error('Endpoint non supporté: ' + endpoint);
  }
  // Router vers l'action métier appropriée
  const { model, method } = params;
  const key = model + ':' + method;

  // Recherche partenaires
  if (key === 'res.partner:search_read') {
    return await callAction('searchPharmacies', { query: extractSearchQuery(params) });
  }
  // Lecture partenaires
  if (key === 'res.partner:read') {
    return await callAction('fetchPartners', { partnerIds: params.args?.[0] || [] });
  }
  // Commandes
  if (key === 'sale.order:search_read') {
    const r = await callAction('fetchOrders', { fromDate: '2024-01-01', toDate: '2030-01-01' });
    // Nouveau format {orders, tourneeField} ou ancien (array direct)
    return Array.isArray(r) ? r : (r?.orders || []);
  }
  if (key === 'sale.order:read') {
    return await callAction('fetchOrderById', { orderId: params.args?.[0]?.[0] });
  }
  if (key === 'sale.order:fields_get') {
    return await callAction('getOrderFields', {});
  }
  // Lignes de commande
  if (key === 'sale.order.line:read') {
    return await callAction('fetchOrderLinesByIds', { lineIds: params.args?.[0] || [] });
  }
  // Produits
  if (key === 'product.product:search_read') {
    return await callAction('searchProducts', { query: extractSearchQuery(params) });
  }
  // Stock
  if (key === 'stock.picking:search_read') {
    return await callAction('fetchPickings', { fromDate: '2024-01-01' });
  }
  if (key === 'stock.move:read') {
    return await callAction('fetchMoves', { moveIds: params.args?.[0] || [] });
  }

  throw new Error('Action non supportée: ' + key);
}

function extractSearchQuery(params) {
  const domain = params.args?.[0] || [];
  for (const cond of domain) {
    if (Array.isArray(cond) && (cond[1] === 'ilike' || cond[1] === 'like')) {
      return String(cond[2] || '').replace(/%/g, '');
    }
  }
  return '';
}

export function resetSession() { /* no-op — sessions serveur */ }

export async function odooAuth() {
  await callAction('getOrderFields', {});
  return { uid: 1, companyId: ELIXIR_COMPANY_ID, companyName: 'Elixir Pharma' };
}

// Fenêtre Odoo : 4 derniers jours depuis Paris, cutoff 6h15
function getOdooWindow() {
  const parts = {};
  new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date()).forEach(p => { parts[p.type] = p.value; });
  const today = parts.year + '-' + parts.month + '-' + parts.day;
  const d = new Date(today + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() - 4);
  const fromDateStr = d.toISOString().split('T')[0];
  // Offset Paris→UTC
  const utcParts = {};
  new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', hour: '2-digit', hour12: false }).formatToParts(new Date()).forEach(p => { utcParts[p.type] = p.value; });
  const offset = (parseInt(parts.hour === '24' ? '0' : parts.hour) - parseInt(utcParts.hour === '24' ? '0' : utcParts.hour) + 24) % 24;
  const utcH = 6 - offset; // 6h15 Paris → UTC
  const fromDate = fromDateStr + ' ' + String(utcH < 0 ? utcH + 24 : utcH).padStart(2, '0') + ':15:00';
  const toDate = new Date().toISOString().replace('T', ' ').substring(0, 19);
  return { fromDate, toDate };
}

export async function fetchTodayOrders(url, companyId, onStatus) {
  onStatus && onStatus('Commandes Elixir Pharma…');

  // Plus de calcul de fenetre cote frontend : le backend fetchOrders calcule
  // tout seul comme fetchSaleOrdersPrepV2 (V2). C est la difference qui faisait
  // que les commandes d hier/avant-hier n apparaissaient pas en V1 alors qu en
  // V2 elles remontaient.
  const result = await callAction('fetchOrders', {});
  // Nouveau format {orders, tourneeField} ou ancien (array direct, retro-compat)
  const orders = Array.isArray(result) ? result : (result?.orders || []);
  const tourneeField = Array.isArray(result) ? null : (result?.tourneeField || null);
  if (!orders.length) return [];

  // invoice_status !== 'invoiced' au lieu de invoice_count === 0 (filtre destructeur).
  // Voir commentaire dans fetchSaleOrdersPrepV2.
  const pendingOrders = orders.filter(o => o.invoice_status !== 'invoiced');
  onStatus && onStatus(pendingOrders.length + ' commandes à préparer — adresses…');
  if (!pendingOrders.length) return [];

  const partnerIds = [...new Set(
    pendingOrders.map(o => Array.isArray(o.partner_id) ? o.partner_id[0] : o.partner_id).filter(Boolean)
  )];
  const partners = {};
  if (partnerIds.length) {
    const pData = await callAction('fetchPartners', { partnerIds });
    pData.forEach(p => { partners[p.id] = p; });
  }

  const map = {}, list = [];
  pendingOrders.forEach(order => {
    const partnerId = Array.isArray(order.partner_id) ? order.partner_id[0] : order.partner_id;
    const partnerName = (Array.isArray(order.partner_id) ? order.partner_id[1] : null) || partners[partnerId]?.name || 'Inconnu';
    const partner = partners[partnerId] || {};
    const adresse = [partner.street, partner.street2, partner.zip, partner.city].filter(Boolean).join(', ');

    let tournee = '';
    if (tourneeField && order[tourneeField]) {
      let raw = order[tourneeField];
      if (Array.isArray(raw)) raw = raw[1] || '';
      tournee = String(raw).trim();
      const v = tournee.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
      if (/IMPERAT/.test(v)) tournee = 'IMPERATIFS';
      else { const m = v.match(/\b([1-9])\b/); if (m) tournee = m[1]; }
    }

    const ref = order.name || '';
    if (!map[partnerId]) {
      map[partnerId] = { partner_id: partnerId, partner_name: partnerName, adresse, tournee, order_refs: [], invoice_refs: [], date_order: order.date_order || '' };
      list.push(map[partnerId]);
    }
    const p = map[partnerId];
    if (ref && !p.order_refs.includes(ref)) p.order_refs.push(ref);
    if (!p.tournee && tournee) p.tournee = tournee;
    if (order.invoice_status === 'to invoice' && ref && !p.invoice_refs.includes(ref)) p.invoice_refs.push(ref);
    if (order.date_order && (!p.date_order || order.date_order < p.date_order)) p.date_order = order.date_order;
  });

  return list;
}

export async function fetchReliquats(url, companyId, onStatus) {
  const daysAgo = new Date();
  daysAgo.setDate(daysAgo.getDate() - 14);
  const fromDate = daysAgo.toISOString().replace('T', ' ').substring(0, 19);

  onStatus && onStatus('Recherche des bons de livraison…');
  const pickings = await callAction('fetchPickings', { fromDate });
  if (!pickings || !pickings.length) return [];

  const origins = [...new Set(pickings.map(p => p.origin).filter(Boolean))];
  const invoices = origins.length ? await callAction('fetchInvoicesByOrigins', { origins }) : [];
  const orderInvoices = {};
  (invoices || []).forEach(inv => {
    if (inv.invoice_origin) orderInvoices[inv.invoice_origin] = (orderInvoices[inv.invoice_origin] || 0) + 1;
  });

  const reliquatPickings = pickings.filter(p => p.origin && (orderInvoices[p.origin] || 0) > 0);
  if (!reliquatPickings.length) return [];

  const allMoveIds = reliquatPickings.flatMap(p => p.move_ids || []);
  const moves = allMoveIds.length ? await callAction('fetchMoves', { moveIds: allMoveIds }) : [];
  const movesByPicking = {};
  (moves || []).forEach(m => {
    const pickId = Array.isArray(m.picking_id) ? m.picking_id[0] : m.picking_id;
    if (!movesByPicking[pickId]) movesByPicking[pickId] = [];
    movesByPicking[pickId].push({
      product_name: Array.isArray(m.product_id) ? m.product_id[1] : (m.name || 'Produit'),
      product_id: Array.isArray(m.product_id) ? m.product_id[0] : m.product_id,
      qty: m.product_uom_qty || 0, qty_done: m.quantity || 0,
    });
  });

  const map2 = {}, list2 = [];
  reliquatPickings.forEach(p => {
    const partnerId = Array.isArray(p.partner_id) ? p.partner_id[0] : p.partner_id;
    const partnerName = (Array.isArray(p.partner_id) ? p.partner_id[1] : null) || 'Inconnu';
    if (!map2[partnerId]) {
      map2[partnerId] = { partner_id: partnerId, partner_name: partnerName, order_refs: [], picking_refs: [], date: p.scheduled_date || '', products: [] };
      list2.push(map2[partnerId]);
    }
    const entry = map2[partnerId];
    if (p.origin && !entry.order_refs.includes(p.origin)) entry.order_refs.push(p.origin);
    if (p.name && !entry.picking_refs.includes(p.name)) entry.picking_refs.push(p.name);
    (movesByPicking[p.id] || []).forEach(m => entry.products.push({ ...m, picking_id: p.id, picking_name: p.name }));
  });
  return list2;
}

export async function fetchOrderLines(url, orderRefs, onStatus) {
  if (!orderRefs || !orderRefs.length) return [];
  onStatus && onStatus('Chargement lignes de commande…');

  const allLines = [];
  for (let i = 0; i < orderRefs.length; i += 50) {
    const batch = orderRefs.slice(i, i + 50);
    const orders = await callAction('fetchOrdersByRefs', { refs: batch });
    if (!orders || !orders.length) continue;
    const lineIds = orders.flatMap(o => o.order_line || []);
    if (!lineIds.length) continue;

    const lines = await callAction('fetchOrderLinesByIds', { lineIds });
    lines.forEach(line => {
      const order = orders.find(o => o.id === (Array.isArray(line.order_id) ? line.order_id[0] : line.order_id));
      const partnerId = order ? (Array.isArray(order.partner_id) ? order.partner_id[0] : order.partner_id) : null;
      allLines.push({
        partner_id: partnerId,
        product_id: Array.isArray(line.product_id) ? line.product_id[0] : line.product_id,
        product_name: Array.isArray(line.product_id) ? line.product_id[1] : (line.name || 'Produit'),
        qty: line.product_uom_qty || 0,
        order_ref: order ? order.name : '',
      });
    });
    onStatus && onStatus('Lignes : ' + allLines.length + ' produits…');
  }
  return allLines;
}

export async function searchPharmacies(url, query) {
  return await callAction('searchPharmacies', { query });
}

export async function fetchOrdersByPartner(url, partnerId, date) {
  return await callAction('fetchOrdersByPartner', { partnerId, date });
}

export async function fetchOrderLinesByOrderId(url, orderId) {
  const orders = await callAction('fetchOrderById', { orderId });
  if (!orders || !orders.length || !orders[0].order_line?.length) return [];
  const lines = await callAction('fetchOrderLinesByIds', { lineIds: orders[0].order_line });
  return (lines || []).map(l => ({
    id: l.id,
    product_id: Array.isArray(l.product_id) ? l.product_id[0] : l.product_id,
    product_name: Array.isArray(l.product_id) ? l.product_id[1] : (l.name || 'Produit'),
    qty: l.product_uom_qty || 0,
  }));
}

export async function searchProducts(url, query) {
  return await callAction('searchProducts', { query });
}

export async function searchSuppliers(url, query) {
  return await callAction('searchSuppliers', { query });
}
